import React, { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useSocket } from './lib/socket';
import { LoadingScreen, PageLoader } from './components/common/LoadingScreen';
import AppLayout from './layouts/AppLayout';
import AuthLayout from './layouts/AuthLayout';
import { clearClientSessionState, hasExplicitLogout } from './lib/sessionCleanup';
import { isAuthFailure, isCsrfFailure } from './lib/authErrors';
import { refreshServerSession } from './features/auth/api/auth.api';
import { clearRememberedCsrfToken, getRetryAfterMs, isRateLimitFailure } from './api/client/http';

let sessionProbePromise: Promise<void> | null = null;

export function SessionBranding() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const branding = useAuthStore((state) => state.user?.organizationBranding);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-navy', isAuthenticated ? branding?.primaryColor || '#1B2A4A' : '#1B2A4A');
    root.style.setProperty('--color-green', isAuthenticated ? branding?.primaryColor || '#1B2A4A' : '#1A6B3C');
    root.style.setProperty('--color-gold', isAuthenticated ? branding?.accentColor || '#F5A623' : '#F5A623');
  }, [isAuthenticated, branding?.primaryColor, branding?.accentColor]);

  return null;
}

async function probeServerSession(): Promise<void> {
  const { accessToken, user } = await refreshServerSession();
  useAuthStore.getState().setAuth(accessToken, user);
}

// Lazy-loaded pages
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const ChangePasswordPage = React.lazy(() => import('./pages/auth/ChangePasswordPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const AcceptInvitationPage = React.lazy(() => import('./pages/auth/AcceptInvitationPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage'));
const MfaSetupPage = React.lazy(() => import('./pages/auth/MfaSetupPage'));

const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const WhatsNewPage = React.lazy(() => import('./pages/updates/WhatsNewPage'));
const LeadsPage = React.lazy(() => import('./pages/leads/LeadsPage'));
const LeadDetailPage = React.lazy(() => import('./pages/leads/LeadDetailPage'));
const LeadEvidencePage = React.lazy(() => import('./pages/leads/LeadEvidencePage'));
const LeadExpertsPage = React.lazy(() => import('./pages/leads/LeadExpertsPage'));
const TendersPage = React.lazy(() => import('./pages/tenders/TendersPage'));
const TenderDetailPage = React.lazy(() => import('./pages/tenders/TenderDetailPage'));

const OpportunityIntelligencePage = React.lazy(() => import('./pages/research/OpportunityIntelligencePage'));
const EventIntelligencePage = React.lazy(() => import('./pages/events/EventIntelligencePage'));

const EvaluationsPage = React.lazy(() => import('./pages/governance/EvaluationsPage'));
const TrainingPage = React.lazy(() => import('./pages/governance/TrainingPage'));
const SkillsAuditPage = React.lazy(() => import('./pages/governance/SkillsAuditPage'));

const MeetingsPage = React.lazy(() => import('./pages/cosec/MeetingsPage'));
const MinutesPage = React.lazy(() => import('./pages/cosec/MinutesPage'));
const EntitiesPage = React.lazy(() => import('./pages/cosec/EntitiesPage'));
const ResolutionsPage = React.lazy(() => import('./pages/cosec/ResolutionsPage'));

const MealProjectsPage = React.lazy(() => import('./pages/social-impact/ProjectsPage'));
const MealProjectDetailPage = React.lazy(() => import('./pages/social-impact/ProjectDetailPage'));

const AiAgentPage = React.lazy(() => import('./pages/ai-agents/AiAgentPage'));
const AgentHistoryPage = React.lazy(() => import('./pages/ai-agents/AgentHistoryPage'));
const PromptTemplatesPage = React.lazy(() => import('./pages/ai-agents/PromptTemplatesPage'));

const NotificationsPage = React.lazy(() => import('./pages/communications/NotificationsPage'));

const ReportsPage = React.lazy(() => import('./pages/reports/ReportsPage'));
const AuditLogPage = React.lazy(() => import('./pages/audit/AuditLogPage'));

const IntegrationsPage = React.lazy(() => import('./pages/settings/IntegrationsPage'));
const OrganizationPage = React.lazy(() => import('./pages/settings/OrganizationPage'));
const ProfilePage = React.lazy(() => import('./pages/settings/ProfilePage'));
const UsersPage = React.lazy(() => import('./pages/admin/UsersPage'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const EvidenceRepositoriesPage = React.lazy(() => import('./pages/admin/EvidenceRepositoriesPage'));

/**
 * Probes the server-controlled HttpOnly refresh cookie once on app boot.
 * Persisted user/auth state is not trusted as proof of a valid session.
 */
function AuthBootstrap({ children }: { children: React.ReactNode }) {
  const { accessToken, user } = useAuthStore();
  const [ready, setReady] = useState(Boolean(accessToken && user));
  const [sessionProbeMessage, setSessionProbeMessage] = useState<string | null>(null);
  const [probeAttempt, setProbeAttempt] = useState(0);

  useEffect(() => {
    if ((accessToken && user) || hasExplicitLogout()) {
      setReady(true);
      return;
    }

    let cancelled = false;
    let retryTimer: number | undefined;
    setSessionProbeMessage(null);

    if (!sessionProbePromise) {
      sessionProbePromise = probeServerSession();
    }

    sessionProbePromise
      .then(() => {
        if (cancelled) return;
        setSessionProbeMessage(null);
        setReady(true);
      })
      .catch((error) => {
        sessionProbePromise = null;
        if (cancelled) return;

        if (isCsrfFailure(error)) {
          // Do not treat a stale double-submit token as a revoked session. The
          // HTTP client performs one recovery attempt; a later retry is safe.
          clearRememberedCsrfToken();
          setSessionProbeMessage('Refreshing session security...');
          retryTimer = window.setTimeout(() => {
            setProbeAttempt((attempt) => attempt + 1);
          }, 2000);
          return;
        }

        if (isAuthFailure(error)) {
          clearClientSessionState();
          setReady(true);
          return;
        }

        if (!isRateLimitFailure(error)) {
          // A network or CORS failure cannot be recovered by repeatedly
          // refreshing the session. Stop the bootstrap loop and let the user
          // sign in again once connectivity/configuration is restored.
          clearClientSessionState();
          setReady(true);
          return;
        }

        const retryAfterMs = getRetryAfterMs(error);
        setSessionProbeMessage('Session check is rate-limited. Please wait a moment...');
        retryTimer = window.setTimeout(() => {
          setProbeAttempt((attempt) => attempt + 1);
        }, retryAfterMs ?? 5000);
      })
      .finally(() => {
        if (sessionProbePromise) {
          sessionProbePromise = null;
        }
      });

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [accessToken, user, probeAttempt]);

  if (sessionProbeMessage) {
    return <LoadingScreen message={sessionProbeMessage} />;
  }

  if (!ready) return <LoadingScreen />;

  return <>{children}</>;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

function RequireRole({ children, roles }: { children: React.ReactNode; roles: string[] }) {
  const { user } = useAuthStore();

  if (!user || !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <>{children}</>;
}

export default function App() {
  // Initialize Socket.io real-time connection
  useSocket();

  return (
    <>
    <SessionBranding />
    <AuthBootstrap>
    <Suspense fallback={<div className="min-h-screen bg-lum-bg-light p-6"><PageLoader /></div>}>
      <Routes>
        {/* Public / Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        </Route>

        {/* Protected App Routes */}
        <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/whats-new" element={<WhatsNewPage />} />

          {/* MFA Setup */}
          <Route path="/mfa/setup" element={<MfaSetupPage />} />

          {/* Leads & Sales Pipeline */}
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/leads/:id" element={<LeadDetailPage />} />
          <Route path="/leads/:id/evidence" element={<LeadEvidencePage />} />
          <Route path="/leads/:id/experts" element={<LeadExpertsPage />} />

          {/* Tenders */}
          <Route path="/tenders" element={<TendersPage />} />
          <Route path="/tenders/:id" element={<TenderDetailPage />} />

          {/* Research */}
          <Route path="/research" element={<OpportunityIntelligencePage />} />
          <Route path="/criteria-requirements" element={<OpportunityIntelligencePage />} />
          <Route path="/events" element={<EventIntelligencePage />} />

          {/* Governance */}
          <Route path="/governance/evaluations" element={<EvaluationsPage />} />
          <Route path="/governance/training" element={<TrainingPage />} />
          <Route path="/governance/skills-audit" element={<SkillsAuditPage />} />

          {/* Company Secretary */}
          <Route path="/cosec/meetings" element={<MeetingsPage />} />
          <Route path="/cosec/minutes" element={<MinutesPage />} />
          <Route path="/cosec/entities" element={<EntitiesPage />} />
          <Route path="/cosec/resolutions" element={<ResolutionsPage />} />

          {/* Social Impact / MEAL */}
          <Route path="/social-impact/projects" element={<MealProjectsPage />} />
          <Route path="/social-impact/projects/:id" element={<MealProjectDetailPage />} />

          {/* AI Agents */}
          <Route path="/agents" element={<AiAgentPage />} />
          <Route path="/agents/history" element={<AgentHistoryPage />} />
          <Route path="/agents/prompts" element={
            <RequireRole roles={['super_admin', 'manager']}>
              <PromptTemplatesPage />
            </RequireRole>
          } />

          {/* Communications */}
          <Route path="/notifications" element={<NotificationsPage />} />

          {/* Reports */}
          <Route path="/reports" element={<ReportsPage />} />

          {/* Audit */}
          <Route path="/audit" element={
            <RequireRole roles={['super_admin', 'auditor']}>
              <AuditLogPage />
            </RequireRole>
          } />

          {/* Settings */}
          <Route path="/settings/profile" element={<ProfilePage />} />
          <Route path="/settings/organization" element={
            <RequireRole roles={['super_admin', 'manager']}>
              <OrganizationPage />
            </RequireRole>
          } />
          <Route path="/settings/integrations" element={
            <RequireRole roles={['super_admin']}>
              <IntegrationsPage />
            </RequireRole>
          } />

          {/* Admin */}
          <Route path="/admin" element={
            <RequireRole roles={['super_admin']}>
              <AdminDashboard />
            </RequireRole>
          } />
          <Route path="/admin/users" element={
            <RequireRole roles={['super_admin']}>
              <UsersPage />
            </RequireRole>
          } />
          <Route path="/admin/evidence-repositories" element={
            <RequireRole roles={['super_admin', 'manager']}>
              <EvidenceRepositoriesPage />
            </RequireRole>
          } />
          <Route path="/admin/evidence-documents" element={<Navigate to="/admin/evidence-repositories?tab=evidence" replace />} />
          <Route path="/admin/expert-governance" element={<Navigate to="/admin/evidence-repositories?tab=experts" replace />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
    </AuthBootstrap>
    </>
  );
}
